import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { getAdvisorSupportingText, type AdvisorFeedback } from '@ppwr/convex/players/types'
import { ADVISOR_WELCOME_COPY } from '@ppwr/convex/players/advisor.app.copy'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { buildActionAdvisorHeadline } from './actionAdvisor.constants'
import {
  buildAdvisorOutcomeFeedback,
  shouldShowAdvisorOutcomeFeedback,
  type AdvisorHandObservation,
  type AdvisorHandOutcome,
  type AdvisorHandResolution,
  type AdvisorOutcomeFeedback,
  type AdvisorOutcomeContext
} from './actionAdvisor.outcome'
import { reconcileAdvisorFeedback } from './actionAdvisor.conversation'

/** Advisor feedback starts MUTED when the advisor loads into a game: the avatar
 * shows grayscale and gives no advice until the player taps it on. The welcome
 * bubble still greets the player (handled in GameContainer) so they know how to
 * turn feedback on. */
export const DEFAULT_ADVISOR_FEEDBACK_ENABLED = false

/** The welcome introduces the advisor at most once per SESSION, for at most
 * this many sessions (persisted) — after that the player has had enough
 * chances to learn the tap-to-unmute toggle. Unmuting the advisor spends the
 * whole budget at once: the welcome's teaching job is done. */
export const ADVISOR_WELCOME_MAX_SESSIONS = 3

/** The coaching level shown in the drawer — also the `coachingLevel`
 * metadata stamped on every advisor analytics event so cohorts can be
 * separated (Beginner vs Intermediate behavior). Same derivation the drawer
 * uses to pick the dropdown's current value. */
export function getAdvisorCoachingLevel(state: Pick<ActionAdvisorState, 'enabled' | 'adviceBeforePlay'>) {
  if (!state.enabled) return 'off' as const
  return state.adviceBeforePlay ? ('beginner' as const) : ('intermediate' as const)
}

export type ActionAdvisorScope = {
  gameId?: Id<'games'>
  handNumber?: number
  userId?: string
}

export function isSameActionAdvisorScope(left?: ActionAdvisorScope, right?: ActionAdvisorScope): boolean {
  if (!(left && right)) {
    return false
  }

  return left.gameId === right.gameId && left.handNumber === right.handNumber && left.userId === right.userId
}

type AdvisorHandReview = {
  scope: ActionAdvisorScope
  observations: AdvisorHandObservation[]
}

export type AdvisorWelcome = {
  feedback: AdvisorFeedback
  headline: string
}

// The welcome strings live in the shared copy bank (advisor.app.copy.ts —
// ADVISOR_WELCOME_COPY) so ADVISOR_OUTPUT.md can enumerate them.
function buildAdvisorWelcomeFeedback(): AdvisorFeedback {
  return {
    grade: 'aligned',
    playerActionLabel: 'Advisor',
    recommendedActionLabel: 'Advisor',
    primaryReason: ADVISOR_WELCOME_COPY.supporting,
    decision: {
      street: 'preflop',
      concept: 'handStrength',
      deviation: 'none',
      importance: 'minor',
      actualAction: 'check',
      recommendedAction: 'check',
      reasonKeys: ['welcome'],
      stateFactors: [],
      copyVariantId: 'advisor:welcome'
    },
    conversation: { kind: 'new' }
  }
}

type ActionAdvisorState = {
  enabled: boolean
  feedbackEnabled: boolean
  /** When true, the advisor speaks at the START of the player's turn (see
   * `useTurnAdvice`) instead of only after they act. Defaults to BEFORE —
   * turn-start advice is the primary experience. Persisted. */
  adviceBeforePlay: boolean
  dismissRequestId: number
  /** Increments whenever the player follows turn-start advice — the avatar
   * answers with a thumbs-up badge instead of a bubble (see ActionAdvisorCard). */
  thumbsUpRequestId: number
  feedback?: AdvisorFeedback
  feedbackScope?: ActionAdvisorScope
  feedbackUpdatedAt?: number
  /** Current turn-start advice, waiting for the player's action. At most one
   * is pending: the first grade after `setAdvice` consumes it (brief
   * confirmation if aligned, deviation copy otherwise — no third bubble).
   * Scoped (game + hand + user) so a stale advice from another game or hand
   * can never be displayed. */
  advice?: AdvisorFeedback
  adviceScope?: ActionAdvisorScope
  /** When the current advice landed — used to detect a turn that passed
   * WITHOUT the player acting (turn-timer auto-action: no client call, so no
   * clear) and drop the now-stale advice. */
  adviceUpdatedAt?: number
  /** Timestamp of the last clear (player acted, advisor toggled off, scope
   * change). Lets a late-arriving advice response detect that the world moved
   * on AFTER its request started, so it can never resurrect over the action's
   * grade (the game subscription lags the action response, so the spot-key
   * check alone does not cover that window). */
  lastClearedAt?: number
  handReview?: AdvisorHandReview
  outcomeFeedback?: AdvisorOutcomeFeedback
  lastOutcomeHandId?: string
  welcome?: AdvisorWelcome
  /** Session latch: the welcome has already been shown in THIS app session —
   * at most one introduction per launch. In-memory only. */
  welcomeShownThisSession: boolean
  /** How many sessions (app launches) have shown the welcome, persisted —
   * the introduction budget. Capped at ADVISOR_WELCOME_MAX_SESSIONS; set
   * straight to the cap when the advisor is activated (unmuted). */
  welcomeSessionCount: number
  /** Recent rendered copy across hands, so phrasing does not repeat back-to-back
   * even when the per-hand observation history resets (e.g. preflop folds in
   * consecutive hands). Session-only; not persisted. */
  recentSupportingTexts: string[]
  recentHeadlines: string[]
}

type ActionAdvisorActions = {
  setEnabled: (enabled: boolean) => void
  setFeedbackEnabled: (feedbackEnabled: boolean) => void
  setAdviceBeforePlay: (adviceBeforePlay: boolean) => void
  requestThumbsUp: () => void
  setAdvice: (advice: AdvisorFeedback, scope: ActionAdvisorScope, requestedAt?: number) => void
  /** Drop a pending advice whose turn passed WITHOUT the player acting —
   * e.g. a turn-timer auto-action (server-side, no client call, so no clear
   * from the action flow). Without this the stale advice would be consumed by
   * the NEXT graded action as a false follow (thumbs-up for nothing). */
  clearAdvice: () => void
  setFeedback: (
    feedback: AdvisorFeedback,
    scope: ActionAdvisorScope,
    updatedAt?: number,
    /** Advice that was pending when the player acted, threaded in by the action
     * flow (which clears the store before executing). Presence makes this grade
     * the advice's follow-up: brief confirmation when aligned, deviation copy
     * otherwise. */
    priorAdvice?: AdvisorFeedback
  ) /** The advice outcome this grade resolved, when the player acted with
   * turn-start advice pending for this exact spot: 'followed' (grade aligned
   * with the shown advice), 'deviated' (grade not aligned), or undefined (no
   * advice was pending, or the pending advice was for a different spot —
   * e.g. a timeout auto-action moved the game on). Used for analytics. */ => 'followed' | 'deviated' | undefined
  queueHandOutcomeFeedback: (
    scope: ActionAdvisorScope,
    outcome: AdvisorHandOutcome,
    resolution: AdvisorHandResolution,
    context: AdvisorOutcomeContext
  ) => void
  requestBubbleDismiss: () => void
  queueAdvisorWelcome: () => void
  /** Admin tool: reset the welcome introduction budget (persisted count +
   * the per-session latch), so the advisor introduces itself again in the
   * next sessions. Used to re-test the welcome flow. */
  resetWelcomeBudget: () => void
  /** Account reset: restores every persisted user setting (enabled switch,
   * coaching level, mute state, welcome budget) to its default so the next
   * login doesn't inherit this account's advisor preferences. Called on
   * logout, mirroring the avatar store's `init`.
   *
   * NOTE: the avatar store solves this class of bug more fundamentally — its
   * preferences are NOT persisted on-device; they live server-side in Convex
   * keyed by userId (`avatarPreferences`) and re-hydrate after every login, so
   * nothing account-scoped can leak between logins on a shared device.
   * This store stays device-local on purpose: advisor settings are low-stakes
   * toggles, and we assume the vast majority of users are not multi-device —
   * same trade-off `read-progress-storage` already makes. If either assumption
   * fails (account switches without a logout: expired-token bounce, app kill;
   * or settings expected to follow a user across devices), swap this local
   * persistence to the avatar pattern — a per-user `advisorPreferences` table
   * hydrated on login — and delete the reset call sites in session-token.ts. */
  resetUserSettings: () => void
  clear: () => void
  clearForScope: (scope: ActionAdvisorScope) => void
}

const initialState: ActionAdvisorState = {
  // Default drawer level: "Advice & Feedback" (enabled + adviceBeforePlay,
  // the "beginner" coaching level) rather than Off — but still MUTED: the
  // avatar starts grayscale and silent (feedbackEnabled below) until the
  // player taps it on. The mute gates everything (advice AND grades), so the
  // default experience greets the player via the welcome bubble and waits
  // for the tap; the drawer level only owns WHAT the advisor does once
  // unmuted.
  enabled: true,
  feedbackEnabled: DEFAULT_ADVISOR_FEEDBACK_ENABLED,
  adviceBeforePlay: true,
  dismissRequestId: 0,
  thumbsUpRequestId: 0,
  feedback: undefined,
  feedbackScope: undefined,
  feedbackUpdatedAt: undefined,
  advice: undefined,
  adviceScope: undefined,
  adviceUpdatedAt: undefined,
  lastClearedAt: undefined,
  handReview: undefined,
  outcomeFeedback: undefined,
  lastOutcomeHandId: undefined,
  welcome: undefined,
  welcomeShownThisSession: false,
  welcomeSessionCount: 0,
  recentSupportingTexts: [],
  recentHeadlines: []
}

/** Only the user's settings and the welcome budget persist across sessions —
 * everything else (feedback, advice, observations, copy memory) is ephemeral
 * game state. The avatar-tap state (`feedbackEnabled`) persists too: with the
 * drawer switch persisted, relaunching with a silent grayscale avatar while the
 * drawer says "Advisor Available ✓" reads as broken (no feedback, no advice)
 * rather than as a mute the player controls by tapping. */
const PERSISTED_KEYS = ['enabled', 'adviceBeforePlay', 'feedbackEnabled', 'welcomeSessionCount'] as const

export const useActionAdvisorStore = create<ActionAdvisorState & ActionAdvisorActions>()(
  persist(
    (set) => ({
      ...initialState,

      setEnabled: (enabled) => set({ enabled }),
      setFeedbackEnabled: (feedbackEnabled) =>
        set((state) => ({
          feedbackEnabled,
          // Activating the advisor (unmuting) is the welcome's whole lesson —
          // spend the entire introduction budget so it never shows again.
          ...(feedbackEnabled ? { welcomeSessionCount: ADVISOR_WELCOME_MAX_SESSIONS } : {})
        })),
      setAdviceBeforePlay: (adviceBeforePlay) => set({ adviceBeforePlay }),
      setAdvice: (advice, scope, requestedAt) =>
        set((state) => {
          // Stale-response guard: a clear that happened after this request
          // started (the player acted, the advisor was toggled off, the scope
          // changed) means the spot this advice describes was already resolved.
          // Landing it would resurrect the pre-play bubble OVER the action's
          // grade — the exact "title never changed" symptom.
          if (requestedAt !== undefined && state.lastClearedAt && state.lastClearedAt >= requestedAt) {
            return state
          }
          const nextSupportingText = getAdvisorSupportingText(advice)
          return {
            advice,
            adviceScope: scope,
            adviceUpdatedAt: Date.now(),
            welcome: undefined,
            // A lingering end-of-hand recap must not leak its headline into
            // the advice bubble: `outcomeFeedback` feeds the card's
            // `headlineOverride`, so the next hand's first pre-play advice
            // would otherwise render with the recap's title.
            outcomeFeedback: undefined,
            recentSupportingTexts: [...state.recentSupportingTexts, nextSupportingText].slice(-RECENT_COPY_MEMORY_SIZE)
          }
        }),
      // `feedback.decision.street` is server-authoritative (set at evaluation time
      // from the board the player acted on); the store does not override it.
      setFeedback: (feedback, scope, updatedAt = Date.now(), priorAdvice) => {
        // The advice outcome this grade resolved (see the action type) — captured
        // so the caller can report it to analytics.
        let resolvedAdviceOutcome: 'followed' | 'deviated' | undefined
        set((state) => {
          const observations = isSameActionAdvisorScope(state.handReview?.scope, scope)
            ? (state.handReview?.observations ?? [])
            : []
          // The first grade after turn-start advice is that advice's follow-up:
          // followed → marked 'confirmed', NO bubble (the avatar answers with a
          // thumbs-up instead — `thumbsUpRequestId`); deviated → the deviation
          // copy. One message per event, then the advice is consumed.
          // `priorAdvice` is threaded in by the action flow, which clears the
          // store before executing the action (the clear would otherwise
          // destroy the context that marks the follow-up). The advice is not a
          // hand observation, so classification against observations is skipped
          // for that follow-up — but the grade itself IS recorded, keeping
          // end-of-hand recaps accurate.
          const pendingAdvice = priorAdvice ?? state.advice
          // Spot identity: a grade only counts as the advice's follow-up when it
          // is the SAME spot the advice described. Advice and grade run the
          // same deterministic policy, so the same spot always agrees on
          // street + recommended action — a mismatch means the pending advice
          // is stale (e.g. the turn was taken by a timeout auto-action) and
          // must not be consumed as a follow (no false 'confirmed' kind, no
          // bubble suppression, no thumbs-up).
          const adviceMatchesSpot =
            Boolean(pendingAdvice) &&
            feedback.decision.street === pendingAdvice?.decision.street &&
            feedback.decision.recommendedAction === pendingAdvice?.decision.recommendedAction
          const followedAdvice = Boolean(adviceMatchesSpot) && feedback.grade === 'aligned'
          const adviceOutcome = adviceMatchesSpot
            ? followedAdvice
              ? ('followed' as const)
              : ('deviated' as const)
            : undefined
          resolvedAdviceOutcome = adviceOutcome
          const reconciledFeedback = reconcileAdvisorFeedback(
            feedback,
            pendingAdvice ? [] : observations,
            adviceMatchesSpot ? pendingAdvice : undefined
          )
          // The recent-copy memory spans hands (unlike `observations`, which are
          // scoped to one hand), so e.g. repeated preflop folds across hands do
          // not reuse the same phrasing.
          const variedFeedback = avoidRecentCopyRepeat(
            reconciledFeedback,
            state.recentSupportingTexts,
            state.recentHeadlines
          )
          const nextSupportingText = getAdvisorSupportingText(variedFeedback)
          const nextHeadline = buildActionAdvisorHeadline(variedFeedback)

          return {
            feedback: variedFeedback,
            feedbackScope: scope,
            feedbackUpdatedAt: updatedAt,
            advice: undefined,
            adviceScope: undefined,
            adviceUpdatedAt: undefined,
            handReview:
              typeof scope.handNumber === 'number'
                ? { scope, observations: [...observations, { feedback: variedFeedback }] }
                : state.handReview,
            outcomeFeedback: undefined,
            welcome: undefined,
            ...(followedAdvice && { thumbsUpRequestId: state.thumbsUpRequestId + 1 }),
            recentSupportingTexts: [...state.recentSupportingTexts, nextSupportingText].slice(-RECENT_COPY_MEMORY_SIZE),
            recentHeadlines: [...state.recentHeadlines, nextHeadline].slice(-RECENT_COPY_MEMORY_SIZE)
          }
        })
        return resolvedAdviceOutcome
      },
      queueHandOutcomeFeedback: (scope, outcome, resolution, context) =>
        set((state) => {
          if (!(scope.gameId && scope.userId && typeof scope.handNumber === 'number')) return state

          const id = `${scope.gameId}:${scope.userId}:${scope.handNumber}:${outcome}`
          if (state.lastOutcomeHandId === id) return state

          const observations = isSameActionAdvisorScope(state.handReview?.scope, scope)
            ? (state.handReview?.observations ?? [])
            : []
          const foldWasCorrect = observations.some(
            (observation) =>
              observation.feedback.grade === 'aligned' &&
              observation.feedback.decision.street === 'preflop' &&
              observation.feedback.decision.actualAction === 'fold'
          )
          if (!shouldShowAdvisorOutcomeFeedback({ context, foldWasCorrect, outcome })) {
            // The hand is over either way — a pending advice can never be
            // followed now, so drop it (otherwise the next hand's first graded
            // action would consume it as a false follow). Stamp the clear like
            // every other advice-clearing path so an in-flight advice response
            // for this hand is discarded on arrival too.
            return {
              handReview: undefined,
              lastOutcomeHandId: id,
              advice: undefined,
              adviceScope: undefined,
              adviceUpdatedAt: undefined,
              lastClearedAt: Date.now()
            }
          }
          const message = buildAdvisorOutcomeFeedback({
            gameId: scope.gameId,
            handNumber: scope.handNumber,
            observations,
            outcome,
            resolution,
            context
          })
          return {
            feedback: message.feedback,
            feedbackScope: scope,
            feedbackUpdatedAt: Date.now(),
            outcomeFeedback: message,
            handReview: undefined,
            lastOutcomeHandId: id,
            advice: undefined,
            adviceScope: undefined,
            adviceUpdatedAt: undefined,
            welcome: undefined
          }
        }),
      requestBubbleDismiss: () => set((state) => ({ dismissRequestId: state.dismissRequestId + 1 })),
      resetWelcomeBudget: () =>
        set({
          welcomeSessionCount: 0,
          welcomeShownThisSession: false
        }),
      // Spread the full initial state (not just PERSISTED_KEYS) so ephemeral
      // game state — pending advice, bubbles, copy memory — dies with the
      // session too. zustand's persist middleware re-persists on every set,
      // so the AsyncStorage record is rewritten with defaults immediately.
      resetUserSettings: () => set(initialState),
      requestThumbsUp: () => set((state) => ({ thumbsUpRequestId: state.thumbsUpRequestId + 1 })),
      clearAdvice: () =>
        set({
          advice: undefined,
          adviceScope: undefined,
          adviceUpdatedAt: undefined,
          // Stamp the clear so any in-flight advice response for the lost turn
          // is discarded on arrival too.
          lastClearedAt: Date.now()
        }),
      queueAdvisorWelcome: () =>
        set((state) => {
          // At most ONE introduction per session, and only while the persisted
          // session budget lasts (or until the advisor is activated).
          if (state.welcomeShownThisSession || state.welcomeSessionCount >= ADVISOR_WELCOME_MAX_SESSIONS) {
            return state
          }
          return {
            welcome: { feedback: buildAdvisorWelcomeFeedback(), headline: ADVISOR_WELCOME_COPY.headline },
            welcomeShownThisSession: true,
            welcomeSessionCount: state.welcomeSessionCount + 1
          }
        }),
      clear: () =>
        set({
          feedback: undefined,
          feedbackScope: undefined,
          feedbackUpdatedAt: undefined,
          advice: undefined,
          adviceScope: undefined,
          adviceUpdatedAt: undefined,
          outcomeFeedback: undefined,
          welcome: undefined,
          lastClearedAt: Date.now()
        }),
      clearForScope: (scope) =>
        set((state) => {
          // Clear whatever belongs to the passed scope — the action feedback
          // and/or a pending advice — and stamp the clear so in-flight advice
          // responses for that scope are discarded on arrival.
          const clearsFeedback = isSameActionAdvisorScope(state.feedbackScope, scope)
          const clearsAdvice = isSameActionAdvisorScope(state.adviceScope, scope)
          if (!clearsFeedback && !clearsAdvice) return state
          return {
            ...(clearsFeedback && {
              feedback: undefined,
              feedbackScope: undefined,
              feedbackUpdatedAt: undefined,
              outcomeFeedback: undefined
            }),
            ...(clearsAdvice && {
              advice: undefined,
              adviceScope: undefined
            }),
            lastClearedAt: Date.now()
          }
        })
    }),
    {
      name: 'action-advisor-settings',
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) =>
        Object.fromEntries(PERSISTED_KEYS.map((key) => [key, state[key]])) as Pick<
          ActionAdvisorState,
          (typeof PERSISTED_KEYS)[number]
        >
    }
  )
)

const RECENT_COPY_MEMORY_SIZE = 3

function avoidRecentCopyRepeat(
  current: AdvisorFeedback,
  recentSupportingTexts: readonly string[],
  recentHeadlines: readonly string[]
): AdvisorFeedback {
  if (recentSupportingTexts.length === 0 && recentHeadlines.length === 0) return current

  let varied = current

  if (recentSupportingTexts.includes(getAdvisorSupportingText(current))) {
    if (
      current.differenceReason &&
      current.differenceReasonAlternative &&
      !recentSupportingTexts.includes(current.differenceReasonAlternative)
    ) {
      varied = { ...current, differenceReason: current.differenceReasonAlternative }
    } else if (
      !current.differenceReason &&
      current.primaryReasonAlternative &&
      !recentSupportingTexts.includes(current.primaryReasonAlternative)
    ) {
      varied = { ...current, primaryReason: current.primaryReasonAlternative }
    }
  }

  if (!recentHeadlines.includes(buildActionAdvisorHeadline(varied))) return varied

  // Bump the copy offset until the headline is one the player has not just seen.
  // Pools are at most ~13 entries and the memory holds 3, so this always finds one.
  const initialOffset = varied.conversation?.copyOffset ?? 0
  for (let increment = 1; increment <= 16; increment += 1) {
    const candidate = {
      ...varied,
      conversation: {
        ...varied.conversation,
        kind: varied.conversation?.kind ?? 'new',
        copyOffset: initialOffset + increment
      }
    }
    if (!recentHeadlines.includes(buildActionAdvisorHeadline(candidate))) return candidate
  }

  return varied
}
